import { Line, lerp } from "./graphs.js";

class BezierCurve {
    constructor({
        context,
        resolution = 5,
        debug = false
    }) {
        this.context = context;
        this.resolution = 2 ** resolution;
        this.debug = debug;

        this.parameter = {
            min: {
                x: Infinity,
                y: Infinity
            },
            max: {
                x: -Infinity,
                y: -Infinity
            }
        };

        this.A = null;
        this.B = null;
        this.C = null;
        this.D = null;
    }
    _pointAt({
        t,
        A = this.A,
        B = this.B,
        C = this.C,
        D = this.D
    }) {
        if (!A || !B || !C) return null;

        if (D) {
            const p0 = lerp(A, B, t);
            const p1 = lerp(B, C, t);
            const p2 = lerp(C, D, t);

            const q0 = lerp(p0, p1, t);
            const q1 = lerp(p1, p2, t);

            return lerp(q0, q1, t);
        }

        const p0 = lerp(A, B, t);
        const p1 = lerp(B, C, t);
        return lerp(p0, p1, t);
    }
    isOnBezierCurve({
        x,
        y,
        A = this.A,
        B = this.B,
        C = this.C,
        D = this.D,
        tolerance = 5
    }) {
        if (!A || !B || !C) return false;

        const distToSegment = (px, py, x1, y1, x2, y2) => {
            const dx = x2 - x1;
            const dy = y2 - y1;
            const len2 = dx * dx + dy * dy;
            if (len2 === 0) return Math.hypot(px - x1, py - y1);
            let t = ((px - x1) * dx + (py - y1) * dy) / len2;
            t = Math.max(0, Math.min(1, t));
            const projX = x1 + t * dx;
            const projY = y1 + t * dy;
            return Math.hypot(px - projX, py - projY);
        };

        let previousPoint = null;
        let minDistance = Infinity;

        for (let t = 0; t <= 1; t += (1 / this.resolution)) {
            const q = this._pointAt({ t, A, B, C, D });
            if (!q) break;

            if (previousPoint) {
                const d = distToSegment(
                    x,
                    y,
                    previousPoint.x,
                    previousPoint.y,
                    q.x,
                    q.y
                );
                if (d < minDistance) minDistance = d;
                if (minDistance <= tolerance) return true;
            }

            previousPoint = q;
        }

        return minDistance <= tolerance;
    }
    draw({
        A,
        B,
        C,
        D = null
    }) {
        this.A = A;
        this.B = B;
        this.C = C;
        this.D = D;
        const ctx = this.context;
        
        const point = ({x, y, l=2, r=4, color='white'}) => {
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = l;

            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.arc(
                x,
                y,
                r,
                0,
                Math.PI * 2
            );
            ctx.stroke();
            ctx.fill();
            ctx.closePath();
        }
        const line = ({A, B, l=2, dash=false, color='white'}) => {
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = l;
            ctx.beginPath();
            if(dash) ctx.setLineDash([16, 6]);
            ctx.moveTo(A.x, A.y);
            ctx.lineTo(B.x, B.y);
            ctx.stroke();
            if(dash) ctx.setLineDash([]);
            ctx.closePath();
        }

        if( this.debug ) {
            point({x: A.x, y: A.y, color: "lime"});
            point({x: B.x, y: B.y});
            if (D) {
                point({x: C.x, y: C.y});
                point({x: D.x, y: D.y, color: "lime"});
            } else {
                point({x: C.x, y: C.y, color: "lime"});
            }

            const a_b_line = Line.fromPoints({A: A, B: B});
            const b_c_line = Line.fromPoints({A: B, B: C});
            if (D) {
                const c_d_line = Line.fromPoints({A: C, B: D});
                line({A: A, B: B, l: 1, dash: true, color: 'red'});
                line({A: B, B: C, l: 1, dash: true, color: 'red'});
                line({A: C, B: D, l: 1, dash: true, color: 'red'});
            } else {
                line({A: A, B: B, l: 1, dash: true, color: 'red'});
                line({A: B, B: C, l: 1, dash: true, color: 'red'});
            }
        }

        // reset bounds for this draw
        this.parameter.min.x = Infinity;
        this.parameter.min.y = Infinity;
        this.parameter.max.x = -Infinity;
        this.parameter.max.y = -Infinity;

        // ctx.strokeStyle = 'white';
        // ctx.beginPath();
        for(let t = 0; t <= 1; t += (1/this.resolution)) {
            const q = this._pointAt({ t, A, B, C, D });
            if (!q) break;

            if( q.x > this.parameter.max.x ) this.parameter.max.x = q.x;
            if( q.x < this.parameter.min.x ) this.parameter.min.x = q.x;
            if( q.y > this.parameter.max.y ) this.parameter.max.y = q.y;
            if( q.y < this.parameter.min.y ) this.parameter.min.y = q.y;

            if (t === 0) {
                ctx.moveTo(q.x, q.y);
            } else {
                ctx.lineTo(q.x, q.y);
            }
        }
        ctx.stroke();
    }
}

export {
    BezierCurve
}
